// VolunteerismInformationForm.js

import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Form,
  Checkbox,
  Modal,
  message,
  Typography,
  Card,
  Col,
  Input,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import Title from "antd/es/typography/Title";
import { util } from '@seims/shared';

const { confirm } = Modal;
const { Text } = Typography;

const VolunteerismInformationForm = ({
  initialValues,
  onSave,
  onCancel,
  volunteerRoles,
  volunteeringReasons,
}) => {
  const [showPreferredOther, setShowPreferredOther] = useState(false);
  const [showOtherReasons, setShowOtherReasons] = useState(false);
  const [form] = Form.useForm();
  const initialFormValuesRef = useRef(null); // To store initial values for comparison
  // Helper function to find lookup object by value or key (needed by VolunteerismInformationForm)
  const findLookupObject = (identifier, lookupArray) => {
    if (!lookupArray || !Array.isArray(lookupArray)) return null;
    return lookupArray.find(
      (item) => item.value === identifier || item.key === identifier
    );
  };

  // useEffect to set initial form values and store them for comparison
  useEffect(() => {
    let transformedValues;
    
    if (initialValues) {
      // Transform initialValues from array of {key, value} objects to array of 'value' strings
      transformedValues = {
        preferredRoles: initialValues.preferredRoles
          ? initialValues?.preferredRoles.map((role) => role.value)
          : [],
        reasons: initialValues.reasons
          ? initialValues?.reasons.map((reason) => reason.value)
          : [],
        otherReasons: initialValues.otherReason || "",
        otherPreferredRoles: initialValues.otherRole || "",
      };
      form.setFieldsValue(transformedValues);

      initialValues.preferredRoles?.find(
        (c) => c.value.toString().toLowerCase() === "other"
      ) && setShowPreferredOther(true);
      initialValues.reasons?.find(
        (c) => c.value.toString().toLowerCase() === "other"
      ) && setShowOtherReasons(true);
    } else {
      // For a new form (no initialValues), initialize with empty arrays
      transformedValues = { preferredRoles: [], reasons: [] };
      form.resetFields(); // Ensure form is truly clear
    }
    initialFormValuesRef.current = transformedValues; // Store transformed values for comparison
  }, [initialValues, form]);

  const onFinish = (values) => {
    // Transform selected 'value' strings back to {key, value} objects for saving
    const transformedData = {
      volunteerism: {
        preferredRoles: values.preferredRoles
          ? values.preferredRoles
              .map((roleValue) => findLookupObject(roleValue, volunteerRoles))
              .filter(Boolean)
              .map((role) => role.key)
          : [],
        reasons: values.reasons
          ? values.reasons
              .map((reasonValue) =>
                findLookupObject(reasonValue, volunteeringReasons)
              )
              .filter(Boolean)
              .map((reason) => reason.key)
          : [],
        id: initialValues.id,
        otherRole: values.otherPreferredRoles,
        otherReason: values.otherReasons
      },
    };



    onSave(transformedData);
    // message.success('Volunteerism Information Saved Successfully!'); // Message is handled by parent now
  };

  // Helper to compare current form values to initial values for dirty state
  const areFormValuesChanged = () => {
    const currentValues = form.getFieldsValue(true); // Get all current form values from the form instance

    const initialRoles = initialFormValuesRef.current?.preferredRoles
      ? [...initialFormValuesRef.current.preferredRoles].sort()
      : [];
    const currentRoles = currentValues.preferredRoles
      ? [...currentValues.preferredRoles].sort()
      : [];

    // Compare preferredRoles (arrays of strings, sorted for reliable comparison)
    if (
      initialRoles.length !== currentRoles.length ||
      !initialRoles.every((val, index) => val === currentRoles[index])
    ) {
      return true;
    }

    const initialReasons = initialFormValuesRef.current?.reasons
      ? [...initialFormValuesRef.current.reasons].sort()
      : [];
    const currentReasons = currentValues.reasons
      ? [...currentValues.reasons].sort()
      : [];

    // Compare reasons (arrays of strings, sorted for reliable comparison)
    if (
      initialReasons.length !== currentReasons.length ||
      !initialReasons.every((val, index) => val === currentReasons[index])
    ) {
      return true;
    }

   
    return false; // No changes found
  };

  const handleCancelClick = () => {
    // Only show the confirmation modal if changes were made
    if (areFormValuesChanged()) {
      confirm({
        title: "Are you sure you want to cancel?",
        icon: <ExclamationCircleOutlined />,
        content:
          "Any unsaved changes will be lost. This action cannot be undone.",
        okText: "Yes, Cancel",
        okType: "danger",
        cancelText: "No, Keep Editing",
        onOk() {
          
          form.resetFields(); // Reset form fields to initial state
          onCancel(); // Call the parent's onCancel prop
        },
        onCancel() {
          
        },
      });
    } else {
      // If no changes, just call onCancel directly
      
      onCancel();
    }
  };

  const handlePreferredRolesChange = (e) => {
    const { checked, value } = e.target;
    if (value.toString().toLowerCase() === "other" && checked) {
      setShowPreferredOther(true);
    } else if (value.toString().toLowerCase() === "other" && !checked) {
      setShowPreferredOther(false);
    }
  };

  const handleReasonsChange = (e) => {
    const { checked, value } = e.target;
    if (value.toString().toLowerCase() === "other" && checked) {
      setShowOtherReasons(true);
    } else if (value.toString().toLowerCase() === "other" && !checked) {
      setShowOtherReasons(false);
    }
  };

  const onCaptionBlur = (e) => {
    const { value, name } = e.target;
    const textValue = util.toPascalText(value);
    form.setFieldsValue({ [name]: textValue });
  };

  return (
    <div
      style={{ display: "flex", justifyContent: "center", padding: "0px" }}
      className="volunteerism-card"
    >
      <Card
        title={<h2 className="card-head-title">VOLUNTEERISM INFORMATION</h2>}
        className="card-main"
        style={{ maxWidth: "", marginBottom: "40px" }}
      >
        <Form
          form={form}
          layout="vertical"
          name="volunteerism_information_form"
          onFinish={onFinish}
          // initialValues are handled in useEffect
        >
          {/* 1. Preferred Volunteer Role(s) */}
          <Title level={4} style={{ marginBottom: "16px" }}>
            Preferred Volunteer Role(s)
          </Title>
          <Col xs={22} sm={23} md={8}>
            <Form.Item
              name="preferredRoles"
              rules={[
                {
                  required: true,
                  message: "Please select at least one preferred role!",
                },
              ]}
            >
              <Checkbox.Group
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }} // Add gap for spacing
              >
                {volunteerRoles.map((option) => (
                  <Checkbox
                    key={option.key}
                    value={option.value}
                    onChange={handlePreferredRolesChange}
                  >
                    {option.value}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Col>
          {showPreferredOther && (
            <Col xs={22} sm={23} md={8}>
              <Form.Item
                name="otherPreferredRoles"
                label="Other preferred role"
                rules={[
                  {
                    required: true,
                    message: "Please enter the other preferred role",
                  },
                ]}
              >
                <Input name="otherPreferredRoles" onBlur={onCaptionBlur} />
              </Form.Item>
            </Col>
          )}

          {/* 2. Reasons for Volunteering */}
          <Title level={4} style={{ marginBottom: "16px" }}>
            Reasons for Volunteering
          </Title>
          <Col xs={22} sm={23} md={8}>
            <Form.Item
              name="reasons"
              rules={[
                {
                  required: true,
                  message:
                    "Please select at least one reason for volunteering!",
                },
              ]}
            >
              <Checkbox.Group
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {volunteeringReasons.map((option) => (
                  <Checkbox
                    key={option.key}
                    value={option.value}
                    onChange={handleReasonsChange}
                  >
                    {option.value}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Col>

          {showOtherReasons && (
            <Col xs={22} sm={23} md={8}>
              <Form.Item
                name="otherReasons"
                label="Other Reason for Volunteering"
                rules={[
                  {
                    required: true,
                    message: "Please enter the other reason for volunteering",
                  },
                ]}
              >
                <Input name="otherReasons" onBlur={onCaptionBlur} />
              </Form.Item>
            </Col>
          )}

          {/* Save and Cancel Buttons */}
          <Form.Item
            style={{
              textAlign: "right",
              marginTop: "24px",
              marginBottom: "0px",
            }}
          >
            <Button
              onClick={handleCancelClick}
              style={{ marginRight: "8px" }}
              className="secondary-volunteer-button"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="primary-volunteer-button"
            >
              Save
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default VolunteerismInformationForm;